import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DOCS, DOC_CATEGORIES } from "@/data/docs";
import { Search, Rocket, LayoutDashboard, TrendingUp, ShieldCheck, UserCog, ArrowRight, Sparkles, Clock, HelpCircle, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/app/AppShell";

const ICON_MAP = { Rocket, LayoutDashboard, TrendingUp, ShieldCheck, UserCog };
const COLOR_MAP = {
  amber: "from-amber-100 to-amber-50 text-amber-700 border-amber-200",
  sky: "from-sky-100 to-sky-50 text-sky-700 border-sky-200",
  emerald: "from-emerald-100 to-emerald-50 text-emerald-700 border-emerald-200",
  slate: "from-slate-100 to-slate-50 text-slate-700 border-slate-200",
  violet: "from-violet-100 to-violet-50 text-violet-700 border-violet-200",
};

export default function DocsHub() {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const { user } = useAuth();

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return null;
    return DOCS.filter((d) =>
      [d.title, d.subtitle, d.category, ...(d.sections || []).flatMap((s) => [s.heading, s.body, ...(s.bullets || [])])]
        .join(" ").toLowerCase().includes(query)
    );
  }, [q]);

  const popular = ["client-portal", "documents", "funding-center", "messages"];

  const body = (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
      {/* Header (marketing / logged-out only — logged-in users get AppShell chrome) */}
      {!user && (
        <header className="border-b border-slate-100 bg-white/70 backdrop-blur-md sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to="/" className="font-display font-black text-lg text-slate-900" data-testid="docs-home">Credit Quack <span className="text-amber-500">Docs</span></Link>
            <nav className="flex items-center gap-4 text-sm font-semibold text-slate-600">
              <Link to="/docs/how-it-works" className="hover:text-slate-900" data-testid="docs-nav-hiw">How it works</Link>
              <Link to="/" className="hover:text-slate-900">Back to site</Link>
            </nav>
          </div>
        </header>
      )}

      {/* Hero + Search */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-amber-200/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-[420px] w-[420px] rounded-full bg-sky-200/40 blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Help Center</p>
          <h1 className="font-display text-5xl sm:text-6xl font-black text-slate-900 mt-3 leading-tight">
            How can we help?
          </h1>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto">
            Search guides, step-by-step walkthroughs, and every feature of the Credit Quack platform.
          </p>

          <div className="mt-8 relative max-w-2xl mx-auto">
            <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search for anything — 'upload document', 'reset password', 'funding score'…"
              className="w-full h-16 pl-14 pr-6 rounded-2xl bg-white border border-slate-200 shadow-lg shadow-slate-900/5 text-base focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-300 transition"
              data-testid="docs-search-input"
            />
          </div>

          {/* Popular searches */}
          {!q && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <span className="text-xs text-slate-500 uppercase tracking-widest font-bold mr-2 self-center">Popular:</span>
              {popular.map((slug) => {
                const d = DOCS.find((x) => x.slug === slug);
                return d ? (
                  <button
                    key={slug}
                    onClick={() => nav(`/docs/${slug}`)}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-amber-300 hover:bg-amber-50 text-slate-700 font-semibold transition"
                    data-testid={`popular-${slug}`}
                  >
                    {d.title}
                  </button>
                ) : null;
              })}
            </div>
          )}
        </div>
      </section>

      {/* Search results OR category browse */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {results ? (
          <div className="max-w-3xl mx-auto" data-testid="docs-search-results">
            <p className="text-sm text-slate-500 mb-4 font-semibold">{results.length} result{results.length === 1 ? "" : "s"} for "{q}"</p>
            <div className="space-y-3">
              {results.map((d) => (
                <Link key={d.slug} to={`/docs/${d.slug}`} className="block p-5 rounded-2xl border border-slate-100 bg-white hover:border-amber-200 hover:shadow-md transition group" data-testid={`docs-result-${d.slug}`}>
                  <p className="text-xs uppercase tracking-widest font-bold text-slate-400">{DOC_CATEGORIES.find((c) => c.slug === d.category)?.name}</p>
                  <p className="font-display text-xl font-bold text-slate-900 mt-1 group-hover:text-amber-600 transition">{d.title}</p>
                  <p className="text-sm text-slate-600 mt-1">{d.subtitle}</p>
                </Link>
              ))}
              {results.length === 0 && (
                <div className="text-center py-12">
                  <HelpCircle size={40} className="mx-auto text-slate-300" />
                  <p className="text-slate-500 mt-3">No exact match — but our AI assistant might know. Click the chat bubble on the bottom right.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Categories grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
              {DOC_CATEGORIES.map((cat) => {
                const Icon = ICON_MAP[cat.icon] || Rocket;
                const docsInCat = DOCS.filter((d) => d.category === cat.slug);
                return (
                  <div key={cat.slug} className={`p-6 rounded-3xl border bg-gradient-to-br ${COLOR_MAP[cat.color]} shadow-sm hover:shadow-md transition`} data-testid={`docs-cat-${cat.slug}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-11 w-11 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                        <Icon size={20} />
                      </div>
                      <p className="font-display font-black text-lg text-slate-900">{cat.name}</p>
                    </div>
                    {docsInCat.length === 0 ? (
                      <p className="text-xs text-slate-500 font-semibold">Coming soon</p>
                    ) : (
                      <ul className="space-y-2">
                        {docsInCat.map((d) => (
                          <li key={d.slug}>
                            <Link to={`/docs/${d.slug}`} className="group inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900" data-testid={`docs-link-${d.slug}`}>
                              {d.title}
                              <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Recently updated */}
            <div className="mt-16">
              <div className="flex items-center gap-2 mb-5">
                <Clock size={18} className="text-slate-400" />
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Recently updated</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {DOCS.slice(0, 4).map((d) => (
                  <Link key={d.slug} to={`/docs/${d.slug}`} className="p-4 rounded-2xl bg-white border border-slate-100 hover:border-amber-200 hover:shadow-md transition group">
                    <p className="text-xs uppercase tracking-widest font-bold text-slate-400">{d.updated}</p>
                    <p className="font-display font-bold text-slate-900 mt-1 group-hover:text-amber-600 transition">{d.title}</p>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-1">{d.subtitle}</p>
                  </Link>
                ))}
              </div>
            </div>

            {/* How it works CTA */}
            <div className="mt-16 rounded-3xl overflow-hidden relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-10">
              <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-amber-500/30 blur-3xl" />
              <div className="relative max-w-2xl">
                <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-300"><Sparkles size={12} /> Guided tour</div>
                <h2 className="font-display text-3xl sm:text-4xl font-black mt-3">See the whole journey — start to funded.</h2>
                <p className="text-slate-300 mt-3">An 11-step immersive walkthrough of exactly how Credit Quack works: from your first Jotform submission to your first funding approval.</p>
                <Link to="/docs/how-it-works" className="inline-flex items-center gap-2 mt-6 bg-amber-500 hover:bg-amber-600 text-white h-12 px-6 rounded-2xl font-black transition" data-testid="hiw-cta">
                  Take the tour <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );

  return user ? <AppShell>{body}</AppShell> : body;
}
